<template>
  <div class="webinfo-page bg-neutral-50 min-h-screen">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <section class="text-center mb-16">
        <h1 class="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
          网页YI起建
        </h1>
        <p class="text-lg md:text-xl text-neutral-500">
          探索更多优秀的AI工具平台和应用集合
        </p>
      </section>

      <section
        v-if="featuredPlatform"
        class="relative overflow-hidden rounded-xl border border-orange-200 bg-gradient-to-br from-amber-400 to-orange-600 p-8 md:p-12 mb-16 shadow-sm"
        @mouseenter="stopFeaturedCarousel"
        @mouseleave="startFeaturedCarousel"
      >
        <button
          v-if="featuredList.length > 1"
          @click="prevFeatured"
          class="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 text-orange-700 hover:bg-white items-center justify-center shadow-sm z-10"
          title="上一个"
        >
          <i class="fa fa-angle-left text-2xl"></i>
        </button>
        <button
          v-if="featuredList.length > 1"
          @click="nextFeatured"
          class="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 text-orange-700 hover:bg-white items-center justify-center shadow-sm z-10"
          title="下一个"
        >
          <i class="fa fa-angle-right text-2xl"></i>
        </button>

        <div class="flex flex-col lg:flex-row lg:items-center gap-10">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-5 mb-8">
              <div
                class="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white/25 flex items-center justify-center text-neutral-900 flex-shrink-0"
              >
                <i
                  :class="['fa', featuredPlatform.icon || 'fa-window-maximize']"
                  class="leading-none"
                  :style="{ fontSize: '48px', lineHeight: 1 }"
                ></i>
              </div>
              <h2 class="text-3xl md:text-4xl font-bold text-neutral-900">
                {{ featuredPlatform.title }}
              </h2>
            </div>

            <p
              class="text-white text-lg md:text-2xl leading-relaxed max-w-5xl mb-10"
            >
              {{ featuredPlatform.description }}
            </p>

            <div class="flex flex-col sm:flex-row gap-5">
              <button
                @click="openSite(featuredPlatform)"
                class="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-white text-orange-700 hover:bg-orange-50 transition-colors text-lg font-medium"
              >
                <i
                  class="mr-3"
                  :class="
                    isInternal(featuredPlatform.url)
                      ? 'fa fa-arrow-right'
                      : 'fa fa-external-link'
                  "
                ></i>
                {{ isInternal(featuredPlatform.url) ? "立即打开" : "立即访问" }}
              </button>
              <div
                class="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-white/20 text-neutral-900 text-lg font-medium"
              >
                <i class="fa fa-star mr-3"></i>
                {{ featuredPlatform.summary || "优秀平台" }}
              </div>
            </div>
          </div>

          <div
            class="hidden lg:flex w-60 h-60 rounded-2xl bg-white/20 items-center justify-center flex-shrink-0"
          >
            <i
              :class="['fa', featuredPlatform.icon || 'fa-window-maximize']"
              class="text-neutral-900 leading-none"
              :style="{ fontSize: '132px', lineHeight: 1 }"
            ></i>
          </div>
        </div>

        <div
          v-if="featuredList.length > 1"
          class="flex items-center justify-center gap-3 mt-10"
        >
          <button
            v-for="(item, index) in featuredList"
            :key="item.id"
            @click="setFeatured(index, true)"
            class="h-2.5 rounded-full transition-all"
            :class="
              index === featuredIndex
                ? 'w-8 bg-white'
                : 'w-2.5 bg-white/45 hover:bg-white/70'
            "
            :title="item.title"
          ></button>
        </div>
      </section>

      <section>
        <div class="flex items-center justify-between gap-4 mb-8">
          <h2 class="text-3xl md:text-4xl font-bold text-neutral-900">
            更多优秀平台
          </h2>
          <div class="hidden md:flex items-center text-sm text-neutral-400">
            <i class="fa fa-th-large mr-2"></i>
            {{ platformList.length }} 个平台
          </div>
        </div>

        <div
          v-if="loading"
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
        >
          <div
            v-for="item in 6"
            :key="item"
            class="bg-white rounded-xl border border-neutral-200 p-6 animate-pulse"
          >
            <div class="w-14 h-14 rounded-lg bg-neutral-200 mb-6"></div>
            <div class="h-7 bg-neutral-200 rounded w-1/2 mb-4"></div>
            <div class="space-y-3 mb-6">
              <div class="h-4 bg-neutral-200 rounded"></div>
              <div class="h-4 bg-neutral-200 rounded"></div>
              <div class="h-4 bg-neutral-200 rounded w-2/3"></div>
            </div>
            <div class="h-5 bg-neutral-200 rounded w-1/3"></div>
          </div>
        </div>

        <div
          v-else-if="platformList.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
        >
          <article
            v-for="platform in platformList"
            :key="platform.id"
            class="bg-white rounded-xl border border-neutral-200 p-6 hover-lift"
          >
            <div class="flex items-start justify-between gap-4 mb-6">
              <div
                class="w-14 h-14 rounded-lg flex items-center justify-center"
                :class="platform.iconBg || 'bg-blue-100'"
              >
                <i
                  :class="[
                    'fa',
                    platform.icon || 'fa-window-maximize',
                    'text-2xl',
                    platform.iconColor || 'text-blue-600',
                  ]"
                ></i>
              </div>
              <span class="flex items-center gap-2">
                <button
                  @click.stop="toggleMyApp(platform.id)"
                  class="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center transition-colors"
                  :class="
                    inMyApps(platform.id)
                      ? 'text-primary bg-primary/10'
                      : 'text-neutral-400 hover:text-primary'
                  "
                  :title="
                    inMyApps(platform.id)
                      ? '已加入我的应用，点击移出'
                      : '加入我的应用'
                  "
                >
                  <i
                    class="text-xs"
                    :class="inMyApps(platform.id) ? 'fa fa-check' : 'fa fa-plus'"
                  ></i>
                </button>
                <span
                  class="px-3 py-1 rounded-full text-xs font-medium"
                  :class="
                    isInternal(platform.url)
                      ? 'bg-violet-50 text-violet-600'
                      : platform.tagClass || 'bg-blue-50 text-blue-600'
                  "
                >
                  {{
                    isInternal(platform.url) ? "本站开发" : platform.tag || "平台"
                  }}
                </span>
              </span>
            </div>

            <h3 class="text-2xl font-bold text-neutral-900 mb-3">
              {{ platform.title }}
            </h3>
            <p class="text-neutral-500 leading-relaxed mb-6 min-h-[72px]">
              {{ platform.description }}
            </p>

            <div
              class="flex items-center justify-between pt-5 border-t border-neutral-200"
            >
              <span class="text-sm text-neutral-500">
                <i class="fa fa-star-o mr-1"></i>
                {{ platform.summary || "应用集合" }}
              </span>
              <button
                @click.stop="openSite(platform)"
                class="inline-flex items-center text-primary hover:text-primary/80 text-sm font-medium"
              >
                {{ isInternal(platform.url) ? "打开" : "访问" }}
                <i
                  class="ml-1"
                  :class="
                    isInternal(platform.url)
                      ? 'fa fa-angle-right'
                      : 'fa fa-external-link'
                  "
                ></i>
              </button>
            </div>
          </article>
        </div>

        <div
          v-else
          class="bg-white rounded-xl border border-neutral-200 p-16 text-center"
        >
          <div
            class="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6 text-neutral-400"
          >
            <i class="fa fa-window-maximize text-3xl"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-900 mb-2">暂无平台</h3>
          <p class="text-neutral-500 text-sm">管理中心添加后会展示在这里</p>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import myAppsMixin from "@/utils/myApps";
import {
  isMiniProgramUrl,
  normalizeSiteUrl,
} from "@/utils/siteLink";

export default {
  name: "WebInfoPage",
  mixins: [myAppsMixin("web")],
  data() {
    return {
      loading: false,
      featuredPlatform: null,
      featuredList: [],
      featuredIndex: 0,
      carouselTimer: null,
      platformList: [],
      /** 已开通免登的站点标识 */
      ssoTargets: [],
    };
  },
  created() {
    this.loadWebInfo();
    this.loadSsoSites();
  },
  beforeUnmount() {
    this.stopFeaturedCarousel();
  },
  methods: {
    /** 以 / 开头的是本站自建网页（站内路由打开），其余为外链（新窗口） */
    isInternal(url) {
      return !!url && String(url).startsWith("/");
    },
    /** 拉取已开通免登的站点标识 */
    loadSsoSites() {
      this.$axios({ url: "sso/sites" })
        .then((res) => {
          if (res.code === 200 && res.data && Array.isArray(res.data.targets)) {
            this.ssoTargets = res.data.targets;
          }
        })
        .catch(() => {});
    },
    /**
     * 打开站点：
     * 1) 站内自建页面 → 路由跳转；
     * 2) 已开通免登的站点（且已登录）→ 生成票据走免登地址；
     * 3) 其它站点 → 直接跳原来的访问网址。
     */
    async openSite(item) {
      const url = ((item && item.url) || "").trim();
      if (!url) return;
      if (this.isInternal(url)) {
        this.$router.push(url);
        return;
      }
      if (isMiniProgramUrl(url)) {
        ElMessage.info("该站点是微信小程序，请在微信中打开");
        return;
      }
      const href = normalizeSiteUrl(url);
      if (!href) {
        ElMessage.warning("站点地址无效，请联系管理员");
        return;
      }
      const useSso =
        localStorage.getItem("token") && this.ssoTargets.indexOf(url) >= 0;
      if (useSso) {
        try {
          const res = await this.$axios({
            url: "sso/ticket?target=" + encodeURIComponent(url),
          });
          if (res.code === 200 && res.data && res.data.url) {
            window.open(res.data.url, "_blank", "noopener");
            return;
          }
        } catch (e) {
          /* 免登失败：回退到原网址 */
        }
      }
      window.open(href, "_blank", "noopener");
    },
    async loadWebInfo() {
      this.loading = true;
      try {
        const allRes = await this.$axios({
          url: "webinfo/all",
          method: "POST",
        });
        const allList =
          allRes.code === 200 && Array.isArray(allRes.data) ? allRes.data : [];
        this.featuredList = allList.filter((item) => item.featured);
        this.featuredIndex = 0;
        this.featuredPlatform = this.featuredList[0] || allList[0] || null;
        this.platformList = allList.filter((item) => !item.featured);
        this.startFeaturedCarousel();
      } catch (error) {
        console.error("获取网页YI起建数据失败:", error);
        this.featuredPlatform = null;
        this.featuredList = [];
        this.platformList = [];
      } finally {
        this.loading = false;
      }
    },
    setFeatured(index, restartTimer = false) {
      if (!this.featuredList[index]) {
        return;
      }
      this.featuredIndex = index;
      this.featuredPlatform = this.featuredList[index];
      if (restartTimer) {
        this.startFeaturedCarousel();
      }
    },
    prevFeatured() {
      const nextIndex =
        this.featuredIndex === 0
          ? this.featuredList.length - 1
          : this.featuredIndex - 1;
      this.setFeatured(nextIndex, true);
    },
    nextFeatured() {
      if (this.featuredList.length <= 1) {
        return;
      }
      const nextIndex = (this.featuredIndex + 1) % this.featuredList.length;
      this.setFeatured(nextIndex);
    },
    startFeaturedCarousel() {
      this.stopFeaturedCarousel();
      if (this.featuredList.length <= 1) {
        return;
      }
      this.carouselTimer = setInterval(() => {
        this.nextFeatured();
      }, 5000);
    },
    stopFeaturedCarousel() {
      if (this.carouselTimer) {
        clearInterval(this.carouselTimer);
        this.carouselTimer = null;
      }
    },
  },
};
</script>
